import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './transaction-result.module.scss';
import useWindowDimensions, { CurrentScreen } from '../../../../hooks/use-window-dimensions';
import { handleCopy } from '../../../../utils';
import { FailedTransactionIcon, PendingTransaction } from '../../../../assets/icons';
import { Button } from '../index';

export interface TransactionResultData {
  status: 'failed' | 'pending';
  reference: string;
  amount: number;
  paymentMethod: string;
  date: string;
  time: string;
}

export interface TransactionResultProps {
  transaction: TransactionResultData;
  onRetry?: () => void;
  onDone: () => void;
  onReportIssue?: () => void;
  onContactSupport?: () => void;
  currency?: string;
}

export const TransactionResultPage: React.FC<TransactionResultProps> = ({
  transaction,
  onRetry,
  onDone,
  onReportIssue,
  onContactSupport,
  currency = 'NGN',
}) => {
  const navigate = useNavigate();
  const currentScreen: CurrentScreen = useWindowDimensions();

  const isFailed = transaction.status === 'failed';
  const isPending = transaction.status === 'pending';

  const handleCopyReference = () => {
    handleCopy(transaction.reference);
  };

  const handleReportIssue = () => {
    if (onReportIssue) {
      onReportIssue();
    } else {
      navigate('/support/report-issue', {
        state: {
          reference: transaction.reference,
          amount: transaction.amount,
          transactionStatus: transaction.status
        }
      });
    }
  };

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport();
    } else {
      navigate('/support/contact', {
        state: {
          reference: transaction.reference,
          transactionStatus: transaction.status
        }
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headSection}>
        {/* Status Icon */}
        <div className={styles.iconContainer}>
          {isFailed && <FailedTransactionIcon />}
          {isPending && <PendingTransaction />}
        </div>

        {/* Title */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            {isFailed ? 'Transaction Failed' : 'Transaction Pending'}
          </h1>
          <p className={styles.subtitle}>
            {isFailed ? 'Your Payment has Failed' : 'Your Payment is Pending'}
          </p>
        </div>
      </div>

      {/* Transaction Details */}
      <div className={styles.detailsSection}>
        {currentScreen.isMobile && (
          <h2 className={styles.sectionTitle}>Transaction Details</h2>
        )}

        <div className={styles.detailsCard}>
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Amount</span>
            <span className={styles.detailValue}>
              {transaction.amount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} {currency}
            </span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Reference</span>
            <div className={styles.referenceValue}>
              <span className={styles.detailValue}>{transaction.reference}</span>
              <button
                className={styles.copyButton}
                onClick={handleCopyReference}
                aria-label="Copy reference"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <rect
                    x="5"
                    y="5"
                    width="9"
                    height="9"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M3 10.5V3C3 2.44772 3.44772 2 4 2H10.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Payment Method</span>
            <span className={styles.detailValue}>{transaction.paymentMethod}</span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Date</span>
            <span className={styles.detailValue}>{transaction.date}</span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Time</span>
            <span className={styles.detailValue}>{transaction.time}</span>
          </div>
        </div>
      </div>

      {/* More Actions */}
      <div className={styles.actionsSection}>
        <h2 className={styles.sectionTitle}>More Actions</h2>

        <div className={styles.actionsList}>
          {/* Retry Action */}
          {onRetry && (
            <button className={styles.actionItem} onClick={onRetry}>
              <div className={styles.actionIcon}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C12.0711 2.5 13.9462 3.35714 15.3033 4.75"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M17.5 2.5V6.5H13.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={styles.actionContent}>
                <span className={styles.actionTitle}>Retry</span>
                {!currentScreen.isMobile && (
                  <span className={styles.actionSubtitle}>
                    Try making the payment again
                  </span>
                )}
              </div>
              <div className={styles.chevronIcon}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.5 15L12.5 10L7.5 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>
          )}

          {/* Report Issue Action */}
          <button className={styles.actionItem} onClick={handleReportIssue}>
            <div className={styles.actionIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <circle cx="14" cy="6" r="2.5" fill="var(--app-landing-orange)" />
                <path
                  d="M10 6V11M10 13.5V14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className={styles.actionContent}>
              <span className={styles.actionTitle}>Report Issue</span>
              {!currentScreen.isMobile && (
                <span className={styles.actionSubtitle}>
                  Report an issue with this transaction
                </span>
              )}
            </div>
            <div className={styles.chevronIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>

          {/* Contact Support Action */}
          <button className={styles.actionItem} onClick={handleContactSupport}>
            <div className={styles.actionIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M7.5 7.5C7.5 5.84315 8.84315 4.5 10.5 4.5H11.5C13.1569 4.5 14.5 5.84315 14.5 7.5V10.5C14.5 12.1569 13.1569 13.5 11.5 13.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M12.5 12.5C12.5 14.1569 11.1569 15.5 9.5 15.5H8.5C6.84315 15.5 5.5 14.1569 5.5 12.5V9.5C5.5 7.84315 6.84315 6.5 8.5 6.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className={styles.actionContent}>
              <span className={styles.actionTitle}>Contact Support</span>
              {!currentScreen.isMobile && (
                <span className={styles.actionSubtitle}>
                  Get help from our support team
                </span>
              )}
            </div>
            <div className={styles.chevronIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M7.5 15L12.5 10L7.5 5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          type="button"
          variant="primary"
          size={'default'}
          onClick={onDone}
          className={styles.continueButton}
          fullWidth={currentScreen.isMobile}
        >
          Done
        </Button>
      </div>
    </div>
  );
};

export default TransactionResultPage;
